import React, { useState, useRef, useEffect } from 'react';
import { nanoid } from 'nanoid';
import { IonIcon, IonList, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonSkeletonText, IonPopover, useIonAlert, IonItem, IonLabel } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { TasksState, ITask } from './TasksState';
import { TaskSorter } from './TaskSorter';
import dayjs from 'dayjs';
import { TaskStatusCalculator, MessageScenario, ITaskStatusContext } from '@innerhive-internal/innerhive-common';
import { createUseStyles } from 'react-jss';
import { chevronDownOutline, chevronUpOutline, todayOutline, calendarOutline, timeOutline, arrowForwardOutline } from 'ionicons/icons';
import { Text } from '../components/Text';
import { IAction } from '../components/Menu';
import { EditIcon, DeleteIcon, CircleCheckIcon, ArrowRightIcon, ArrowLeftIcon } from '../theme/Icons';
import { Href } from '../components/Href';
import { TaskListItem } from './TaskListItem';
import { shouldShowTaskWrapUp, shouldShowCelebration } from './taskWrapUp/taskWrapUpUtils';
import { TaskWrapUpCard } from './taskWrapUp/TaskWrapUpCard';
import { CelebrationModal } from './taskWrapUp/CelebrationModal';
import { Logger } from '../support/Logger';
import { useScrollIntoView } from '../utils/useScrollIntoView';
import { isPlatform } from '../utils/isPlatform';

interface TasksListProps {
    heightOffset?: number;
    onEditTask: (taskId: string, source?: 'row-click' | 'menu-edit') => void;
    onDeleteTask: (taskId: string) => void;
    onAddTask?: (dueDate?: string, source?: 'add-button' | 'task-review-card') => void;
    filteredTasks?: ITask[];
    highlightTaskId?: string;
    onHighlightComplete?: () => void;
}

const useStyles = createUseStyles({
    dateHeading: {
        padding: 6,
        fontWeight: 500,
        textTransform: 'uppercase',
        background: 'var(--ion-color-step-50)',
        color: 'var(--ion-color-step-600, #666666)'
    },

    todayHeading: {
        color: 'var(--ion-color-secondary)',
    },

    showMoreButton: {
        margin: '16px',
        '--background': 'var(--ion-color-light)',
        '--color': 'var(--ion-color-primary)'
    },

    summaryCard: {
        margin: '16px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },

    encouragingMessage: {
        fontSize: '14px',
        fontStyle: 'italic',
        opacity: 0.9,
        marginTop: '8px'
    },

    loadingMask: {
        width: '100%',
        position: 'absolute',
        paddingInlineStart: 'var(--padding-start)',
        paddingInlineEnd: 'var(--padding-end)',
        left: 0,
        right: 0,
        background: 'var(--ion-background-color, #fff)',
        zIndex: 10
    }
});

export const TasksList: React.FC<TasksListProps> = ({ heightOffset = 0, onEditTask, onDeleteTask, onAddTask, filteredTasks, highlightTaskId, onHighlightComplete }) => {
    const classes = useStyles();
    const log = useInstance(Logger);

    const [presentAlert] = useIonAlert();
    const [loadingOperations, setLoadingOperations] = useState<Set<string>>(new Set());
    const [activeHighlightId, setActiveHighlightId] = useState<string | undefined>(undefined);
    const highlightedTaskRef = useRef<HTMLDivElement | null>(null);
    const scrollIntoView = useScrollIntoView();

    // Callback ref that triggers scroll when the highlighted element is mounted
    const highlightedTaskCallbackRef = (element: HTMLDivElement | null) => {
        highlightedTaskRef.current = element;
        if (element && activeHighlightId) {
            scrollIntoView(highlightedTaskRef);
        }
    };

    // Tasks status UI state
    const [showCelebrationModal, setShowCelebrationModal] = useState(false);
    const [scenario, setScenario] = useState<MessageScenario | null>(null);
    const [context, setContext] = useState<ITaskStatusContext | null>(null);
    const prevTasksRef = useRef<ITask[] | null>(null);

    const tasksState = useInstance(TasksState);
    useSubscription(() => tasksState);

    const taskSorter = useInstance(TaskSorter);

    // Calculate task status directly from current app state for evening mode UI
    useEffect(() => {
        if (shouldShowTaskWrapUp() && tasksState.isLoaded()) {
            const calculator = new TaskStatusCalculator();
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            const taskCounts = calculator.calculateTaskCounts(tasksState.state.tasks, timezone);
            const taskContext = calculator.buildTaskStatusContext(tasksState.state.tasks, timezone);
            const messageScenario = calculator.determineMessageScenario(taskCounts);

            setScenario(messageScenario);
            setContext(taskContext);

            // Check if celebration should trigger (when last overdue/due-today task is completed/skipped)
            if (prevTasksRef.current && shouldShowCelebration(prevTasksRef.current, tasksState.state.tasks)) {
                setShowCelebrationModal(true);
            }

            prevTasksRef.current = tasksState.state.tasks;
        }
    }, [tasksState, tasksState.state.tasks]);

    // Handle task highlighting from notification deep link
    useEffect(() => {
        // Wait for tasks to load AND the specific task to exist in the list
        const taskExists = tasksState.isLoaded() &&
            tasksState.state.tasks.some(t => t.taskId === highlightTaskId);

        if (highlightTaskId && taskExists) {
            // Delay highlight to start after scroll completes on native
            // Uses same platform-aware delays as useScrollIntoView
            const scrollDelay = isPlatform('desktop') ? 10 : isPlatform('capacitor') ? 1000 : 350;

            setTimeout(() => {
                setActiveHighlightId(highlightTaskId);
            }, scrollDelay);

            // Clear the highlight after animation completes (2s animation + buffer + scroll delay)
            setTimeout(() => {
                setActiveHighlightId(undefined);
                onHighlightComplete?.();
            }, scrollDelay + 2500);
        }
    }, [highlightTaskId, tasksState.isLoaded(), tasksState.state.tasks]); // eslint-disable-line react-hooks/exhaustive-deps


    const getTasksByDateFromList = (tasks: ITask[]) => {
        const today = dayjs().startOf('day');
        const tomorrow = today.add(1, 'day');
        const dayAfter = today.add(2, 'day');

        const groupedTasks: Record<string, ITask[]> = {
            overdue: [],
            today: [],
            tomorrow: [],
            dayAfter: [],
            future: []
        };

        tasks.forEach(task => {
            if (!task.dueDate) return;
            const taskDate = dayjs(task.dueDate).startOf('day');

            if (taskDate.isBefore(today)) {
                // Include overdue tasks that are incomplete OR completed/skipped today
                if (!task.completed && !task.skippedAt) {
                    groupedTasks.overdue.push(task);
                } else if (task.completed && task.completedAt) {
                    const completedDate = dayjs(task.completedAt).startOf('day');
                    if (completedDate.isSame(today)) {
                        groupedTasks.overdue.push(task);
                    }
                } else if (task.skippedAt) {
                    const skippedDate = dayjs(task.skippedAt).startOf('day');
                    if (skippedDate.isSame(today)) {
                        groupedTasks.overdue.push(task);
                    }
                }
            } else if (taskDate.isSame(today)) {
                groupedTasks.today.push(task);
            } else if (taskDate.isSame(tomorrow)) {
                groupedTasks.tomorrow.push(task);
            } else if (taskDate.isSame(dayAfter)) {
                groupedTasks.dayAfter.push(task);
            } else if (taskDate.isAfter(dayAfter)) {
                groupedTasks.future.push(task);
            }
        });

        // Sort tasks within each date group by date/time, then by manual ordering (same logic as TasksState)
        Object.keys(groupedTasks).forEach(key => {
            groupedTasks[key] = taskSorter.sortTasks(groupedTasks[key]);
        });

        return groupedTasks;
    };

    // Use filtered tasks if provided, otherwise get from state
    const tasksByDate = filteredTasks ? getTasksByDateFromList(filteredTasks) : tasksState.getTasksByDate();
    const overdueTasks = filteredTasks ? tasksByDate.overdue : tasksState.getOverdueTasks();
    const stats = tasksState.getCompletionStats();
    const loaded = tasksState.state.loaded;

    // Count incomplete overdue tasks
    const incompleteOverdueCount = overdueTasks.filter(task => !task.completed && !task.skippedAt).length;

    const [showAllUpcoming, setShowAllUpcoming] = useState(false);
    const [showOverdue, setShowOverdue] = useState(false);
    const [isReschedulePopoverOpen, setIsReschedulePopoverOpen] = useState(false);

    const rescheduleButtonIdRef = useRef(nanoid());

    const handleToggleComplete = async (taskId: string, event?: React.MouseEvent) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        await tasksState.toggleTaskCompletion(taskId);
    };

    const handleRescheduleOverdue = async (rescheduleOption: 'today' | 'tomorrow' | 'this-weekend' | 'next-week') => {
        setIsReschedulePopoverOpen(false);
        const operationId = 'reschedule-overdue';
        setLoadingOperations(prev => new Set(prev).add(operationId));
        try {
            await tasksState.rescheduleOverdueTasks(rescheduleOption);
        } finally {
            setLoadingOperations(prev => {
                const newSet = new Set(prev);
                newSet.delete(operationId);
                return newSet;
            });
        }
    };


    const getEncouragingMessage = () => {
        const todayCompleted = tasksByDate.today.filter(t => t.completed).length;
        const todayTotal = tasksByDate.today.length;
        const percentage = todayTotal > 0 ? Math.round((todayCompleted / todayTotal) * 100) : 0;

        if (todayTotal === 0) {
            return 'Ready for a productive day! Add your first task to get started.';
        } else if (percentage === 100) {
            return "🎉 Amazing! You've completed all of today's care tasks. You're doing incredible work.";
        } else if (percentage >= 75) {
            return "✨ You're almost there! Just a few more tasks and you'll have everything wrapped up for today.";
        } else if (percentage >= 50) {
            return "💪 Great progress! You're halfway through today's tasks. Every step makes a difference.";
        } else if (percentage >= 25) {
            return "🌟 You're off to a good start! Each task you complete is an act of love and care.";
        } else {
            return "☀️ Starting fresh today! Remember, you don't have to do everything at once. One task at a time.";
        }
    };



    const getTodayStats = () => {
        const todayTasks = tasksByDate.today;
        const completed = todayTasks.filter(t => t.completed).length;
        const total = todayTasks.length;
        return { completed, total };
    };

    const handleTaskClick = (taskId: string) => {
        onEditTask(taskId, 'row-click');
    };

    const handleDeleteTask = (task: ITask) => {
        const destructiveRole = 'destructive';

        // Special confirmation for active recurring tasks
        const isActiveRecurringTask = task.recurrenceRule && !task.completed;
        const message = isActiveRecurringTask
            ? `"${task.description}" is a recurring task. Deleting will stop all future recurrences. You can skip it instead if you just want to skip this instance. Continue with delete?`
            : `"${task.description}" will be deleted and cannot be recovered.`;

        presentAlert({
            header: 'Delete Task?',
            message: message,
            id: 'delete-task-confirmation',
            buttons: [{
                text: 'Delete',
                role: destructiveRole,
                handler: () => {
                    onDeleteTask(task.taskId);
                }
            }, {
                text: 'Cancel',
                role: 'cancel'
            }]
        });
    };

    const handleSkipTask = async (task: ITask) => {
        const operationId = `skip-${task.taskId}`;
        setLoadingOperations(prev => new Set(prev).add(operationId));
        try {
            await tasksState.skipTask(task.taskId);
        } catch (error: any) {
            error.message = `Failed to skip task: ${error.message}`;
            log.error(error);
            presentAlert({
                header: 'Skip Failed',
                message: 'Failed to skip this task. Please try again.',
                cssClass: 'drawer-alert',
                buttons: [{
                    text: 'OK',
                    role: 'cancel'
                }]
            });
        } finally {
            setLoadingOperations(prev => {
                const newSet = new Set(prev);
                newSet.delete(operationId);
                return newSet;
            });
        }
    };

    const handleUnskipTask = async (task: ITask) => {
        try {
            await tasksState.unskipTask(task.taskId);
        } catch (error: any) {
            error.message = `Failed to undo skip task: ${error.message}`;
            log.error(error);
            presentAlert({
                header: 'Undo Skip Failed',
                message: 'Failed to undo skip for this task. Please try again.',
                cssClass: 'drawer-alert',
                buttons: [{
                    text: 'OK',
                    role: 'cancel'
                }]
            });
        }
    };

    const handleCompleteForever = async (task: ITask) => {
        const operationId = `complete-forever-${task.taskId}`;
        setLoadingOperations(prev => new Set(prev).add(operationId));
        try {
            await tasksState.completeForever(task.taskId);
        } catch (error: any) {
            error.message = `Failed to complete forever: ${error.message}`;
            log.error(error);
            presentAlert({
                header: 'Complete Forever Failed',
                message: 'Failed to complete this task forever. Please try again.',
                cssClass: 'drawer-alert',
                buttons: [{
                    text: 'OK',
                    role: 'cancel'
                }]
            });
        } finally {
            setLoadingOperations(prev => {
                const newSet = new Set(prev);
                newSet.delete(operationId);
                return newSet;
            });
        }
    };

    const getTaskActions = (task: ITask): IAction[] => {
        const actions: IAction[] = [];

        // Always show edit if task has self link (edit permission)
        if (task.links?.self) {
            actions.push({
                Icon: EditIcon,
                text: 'Edit',
                handler: () => onEditTask(task.taskId, 'menu-edit')
            });
        }

        // Show skip option for all active tasks (recurring and non-recurring)
        if (!task.completed && !task.skippedAt && task.links?.self) {
            actions.push({
                Icon: ArrowRightIcon,
                text: 'Skip',
                handler: () => handleSkipTask(task)
            });
        }

        // Show undo skip option for skipped tasks (recurring or not)
        if (task.skippedAt && !task.completed && task.links?.self) {
            actions.push({
                Icon: ArrowLeftIcon,
                text: 'Undo Skip',
                handler: () => handleUnskipTask(task)
            });
        }

        // Show "Complete Forever" for recurring tasks that are active (not completed or skipped)
        if (task.recurrenceRule && !task.completed && !task.skippedAt && task.links?.self) {
            actions.push({
                Icon: CircleCheckIcon,
                text: 'Complete Forever',
                handler: () => handleCompleteForever(task)
            });
        }


        // Only show delete if task has delete link (delete permission)
        if (task.links?.delete) {
            actions.push({
                Icon: DeleteIcon,
                text: 'Delete',
                role: 'destructive',
                handler: () => handleDeleteTask(task)
            });
        }

        return actions;
    };

    const renderTaskItem = (task: ITask, zone: string) => {
        // Check if this task has any ongoing operations
        const isLoading = loadingOperations.has(`skip-${task.taskId}`) ||
                         loadingOperations.has(`complete-forever-${task.taskId}`) ||
                         loadingOperations.has('reschedule-overdue');

        const isHighlighted = task.taskId === activeHighlightId;

        return (
            <TaskListItem
                key={task.taskId}
                itemRef={isHighlighted ? highlightedTaskCallbackRef : undefined}
                task={task}
                zone={zone}
                isLoading={isLoading}
                isHighlighted={isHighlighted}
                onTaskClick={handleTaskClick}
                onToggleComplete={handleToggleComplete}
                getTaskActions={getTaskActions}
            />
        );
    };

    const isEmpty = stats.total === 0 && loaded;
    const todayStats = getTodayStats();
    const hasTasksToday = tasksByDate.today.length > 0;
    const hasTasksTomorrow = tasksByDate.tomorrow.length > 0;

    // Show empty state only if loaded and truly empty
    if (isEmpty) {
        return (
            <div style={{ paddingBottom: '20px' }} data-id="tasks-empty-state">
                <div style={{ paddingTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div><Text>Add tasks to keep track of important care activities</Text></div>
                    <img src="/assets/images/calendar/empty-appointments.svg" alt="no tasks" style={{ width: '100%' }} />
                </div>
            </div>
        );
    }

    return (
        <div style={{ paddingBottom: '20px', position: 'relative' }}>
            {/* Skeleton Loading Mask */}
            <div
                className={classes.loadingMask}
                style={{
                    top: heightOffset,
                    height: `calc(100% + ${heightOffset}px)`,
                    display: loaded ? 'none' : 'block'
                }}
            >
                <div>
                    {/* Progress card skeleton */}
                    <IonSkeletonText animated style={{ '--border-radius': '12px', height: '120px', marginBottom: '16px' }}></IonSkeletonText>

                    {/* Today section skeleton */}
                    <IonSkeletonText animated style={{ '--border-radius': '8px', height: 31, marginBottom: '8px' }}></IonSkeletonText>
                    <div style={{ paddingLeft: 16, paddingTop: 1 }}>
                        <IonSkeletonText animated style={{ '--border-radius': '8px', height: 48, marginBottom: '8px' }}></IonSkeletonText>
                        <IonSkeletonText animated style={{ '--border-radius': '8px', height: 48, marginBottom: '8px' }}></IonSkeletonText>
                        <IonSkeletonText animated style={{ '--border-radius': '8px', height: 48 }}></IonSkeletonText>
                    </div>

                    {/* Tomorrow section skeleton */}
                    <IonSkeletonText animated style={{ '--border-radius': '8px', height: 31, marginTop: '16px', marginBottom: '8px' }}></IonSkeletonText>
                    <div style={{ paddingLeft: 16, paddingTop: 1 }}>
                        <IonSkeletonText animated style={{ '--border-radius': '8px', height: 48, marginBottom: '8px' }}></IonSkeletonText>
                        <IonSkeletonText animated style={{ '--border-radius': '8px', height: 48 }}></IonSkeletonText>
                    </div>
                </div>
            </div>

            {/* Task Wrap-Up Card - Only show during evening hours (7:05pm-midnight EST) or when simulated */}
            {loaded && shouldShowTaskWrapUp() && (
                <>
                    {scenario && context ? (
                        <TaskWrapUpCard
                            scenario={scenario}
                            context={context}
                            onAction={(action) => {
                                if (action === 'reschedule-all') {
                                    tasksState.rescheduleAllToTomorrow('task-review-card');
                                } else if (action === 'skip-remaining') {
                                    tasksState.skipRemaining('task-review-card');
                                }
                            }}
                            onAddTask={() => {
                                const tomorrow = dayjs().add(1, 'day').startOf('day').toISOString();
                                onAddTask?.(tomorrow, 'task-review-card');
                            }}
                        />
                    ) : null}
                    {showCelebrationModal && (
                        <CelebrationModal
                            isOpen={showCelebrationModal}
                            onClose={() => setShowCelebrationModal(false)}
                        />
                    )}
                </>
            )}

            {/* Dashboard Summary - Only show when loaded and NOT during task wrap-up time */}
            {loaded && !shouldShowTaskWrapUp() && (
                <IonCard className={classes.summaryCard}>
                    <IonCardHeader>
                        <IonCardTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>Today's Progress</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <CircleCheckIcon color={todayStats.total > 0 && todayStats.completed === todayStats.total ? 'success' : 'medium'} />
                                <Text color={todayStats.total > 0 && todayStats.completed === todayStats.total ? 'success' : 'medium'}>
                                    <div style={{ fontSize: 14 }}>
                                        {todayStats.total === 0 ? (
                                            'No tasks'
                                        ) : todayStats.completed > 0 && todayStats.completed < todayStats.total ? (
                                            `${todayStats.completed}/${todayStats.total} tasks`
                                        ) : todayStats.completed === todayStats.total ? (
                                            `${todayStats.total} ${todayStats.total === 1 ? 'task completed' : 'tasks completed'}`
                                        ) : (
                                            `${todayStats.total} ${todayStats.total === 1 ? 'task' : 'tasks'}`
                                        )}
                                    </div>
                                </Text>
                            </div>
                        </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        <div className={classes.encouragingMessage} style={{ color: 'var(--ion-color-medium)' }}>
                            {getEncouragingMessage()}
                        </div>
                    </IonCardContent>
                </IonCard>
            )}


            {/* Task sections - Only show when loaded */}
            {loaded && (
                <>
                    {/* Overdue tasks - Only show when they exist */}
                    {overdueTasks.length > 0 && (
                        <div style={{ marginBottom: showOverdue ? '0' : '16px' }}>
                            <div
                                className={classes.dateHeading}
                                data-id="overdue-header"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer'
                                }}
                                onClick={() => setShowOverdue(!showOverdue)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span>
                                        {incompleteOverdueCount === 0
                                            ? 'REVIEWED TODAY'
                                            : incompleteOverdueCount === 1
                                                ? 'NEEDS ATTENTION'
                                                : 'NEED ATTENTION'}
                                    </span>
                                    <IonIcon
                                        icon={showOverdue ? chevronUpOutline : chevronDownOutline}
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                                <IonButton
                                    fill="clear"
                                    size="small"
                                    id={rescheduleButtonIdRef.current}
                                    data-id="reschedule-button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsReschedulePopoverOpen(true);
                                    }}
                                    style={{
                                        '--color': 'var(--ion-color-primary)',
                                        '--padding-start': '0',
                                        '--padding-end': '0',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        height: 'auto',
                                        minHeight: 'auto'
                                    }}
                                >
                                    Reschedule
                                </IonButton>
                            </div>
                            {showOverdue && (
                                <IonList data-id="tasks-date-group-overdue">
                                    {overdueTasks.map(task => renderTaskItem(task, 'overdue'))}
                                </IonList>
                            )}
                        </div>
                    )}

                    {/* Reschedule Popover - Only render when there are overdue tasks */}
                    {overdueTasks.length > 0 && (
                        <IonPopover
                            keepContentsMounted
                            trigger={rescheduleButtonIdRef.current}
                            alignment="end"
                            isOpen={isReschedulePopoverOpen}
                            onDidDismiss={() => setIsReschedulePopoverOpen(false)}
                            data-id="reschedule-popover"
                        >
                            <IonList style={{ padding: 0 }}>
                                <IonItem button lines="full" onClick={() => handleRescheduleOverdue('today')} style={{ '--min-height': '48px' }}>
                                    <IonIcon
                                        icon={todayOutline}
                                        slot="start"
                                        color="secondary"
                                        style={{ fontSize: '16px', marginInlineEnd: '12px' }}
                                    />
                                    <IonLabel>
                                        <h3 style={{ fontSize: '14px', margin: 0 }}>Today</h3>
                                        <p style={{ fontSize: '12px', margin: 0 }}>{dayjs().format('dddd')}</p>
                                    </IonLabel>
                                </IonItem>
                                <IonItem button lines="full" onClick={() => handleRescheduleOverdue('tomorrow')} style={{ '--min-height': '48px' }}>
                                    <IonIcon
                                        icon={arrowForwardOutline}
                                        slot="start"
                                        color="tertiary"
                                        style={{ fontSize: '16px', marginInlineEnd: '12px' }}
                                    />
                                    <IonLabel>
                                        <h3 style={{ fontSize: '14px', margin: 0 }}>Tomorrow</h3>
                                        <p style={{ fontSize: '12px', margin: 0 }}>{dayjs().add(1, 'day').format('dddd')}</p>
                                    </IonLabel>
                                </IonItem>
                                <IonItem button lines="full" onClick={() => handleRescheduleOverdue('this-weekend')} style={{ '--min-height': '48px' }}>
                                    <IonIcon
                                        icon={calendarOutline}
                                        slot="start"
                                        color="tertiary"
                                        style={{ fontSize: '16px', marginInlineEnd: '12px' }}
                                    />
                                    <IonLabel>
                                        <h3 style={{ fontSize: '14px', margin: 0 }}>This Weekend</h3>
                                        <p style={{ fontSize: '12px', margin: 0 }}>{dayjs().day(6).format('dddd')}</p>
                                    </IonLabel>
                                </IonItem>
                                <IonItem button lines="none" onClick={() => handleRescheduleOverdue('next-week')} style={{ '--min-height': '48px' }}>
                                    <IonIcon
                                        icon={timeOutline}
                                        slot="start"
                                        color="tertiary"
                                        style={{ fontSize: '16px', marginInlineEnd: '12px' }}
                                    />
                                    <IonLabel>
                                        <h3 style={{ fontSize: '14px', margin: 0 }}>Next Week</h3>
                                        <p style={{ fontSize: '12px', margin: 0 }}>{dayjs().add(1, 'week').startOf('week').add(1, 'day').format('dddd MMM D')}</p>
                                    </IonLabel>
                                </IonItem>
                            </IonList>
                        </IonPopover>
                    )}

                    {/* Today's tasks - Always show section */}
                    <div className={`${classes.dateHeading} ${classes.todayHeading}`} data-id="today-header">
                        TODAY, {dayjs().format('MMM D').toUpperCase()}
                    </div>
                    <IonList data-id="tasks-date-group-today">
                        {hasTasksToday ? (
                            tasksByDate.today.map(task => renderTaskItem(task, 'today'))
                        ) : (
                            <IonItem lines="none">
                                <IonLabel className="ion-text-wrap">
                                    <Text>
                                        No tasks today. {onAddTask && <Href onClick={() => onAddTask(dayjs().startOf('day').toISOString())}>Add a task.</Href>}
                                    </Text>
                                </IonLabel>
                            </IonItem>
                        )}
                    </IonList>

                    {/* Tomorrow's tasks - Always show section */}
                    <div className={classes.dateHeading} data-id="tomorrow-header">
                        TOMORROW, {dayjs().add(1, 'day').format('dddd, MMM D').toUpperCase()}
                    </div>
                    <IonList data-id="tasks-date-group-tomorrow">
                        {hasTasksTomorrow ? (
                            tasksByDate.tomorrow.map(task => renderTaskItem(task, 'tomorrow'))
                        ) : (
                            <IonItem lines="none">
                                <IonLabel className="ion-text-wrap">
                                    <Text>
                                        No tasks tomorrow. {onAddTask && <Href onClick={() => onAddTask(dayjs().startOf('day').add(1, 'day').toISOString())}>Add a task.</Href>}
                                    </Text>
                                </IonLabel>
                            </IonItem>
                        )}
                    </IonList>

                    {/* Upcoming tasks - Only show if they exist */}
                    {(tasksByDate.dayAfter.length > 0 || tasksByDate.future.length > 0) && (
                        <>
                            <div className={classes.dateHeading}>
                                Upcoming
                            </div>
                            <IonList data-id="tasks-date-group-upcoming">
                                {[...tasksByDate.dayAfter, ...tasksByDate.future]
                                    .slice(0, showAllUpcoming ? undefined : 3)
                                    .map(task => renderTaskItem(task, 'dayAfter'))}
                            </IonList>
                            {[...tasksByDate.dayAfter, ...tasksByDate.future].length > 3 && (
                                <IonButton
                                    expand="block"
                                    fill="clear"
                                    className={classes.showMoreButton}
                                    onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                                >
                                    <IonIcon
                                        icon={showAllUpcoming ? chevronUpOutline : chevronDownOutline}
                                        slot="start"
                                    />
                                    {showAllUpcoming ? 'Show Less' : `Show ${[...tasksByDate.dayAfter, ...tasksByDate.future].length - 3} More`}
                                </IonButton>
                            )}
                        </>
                    )}
                </>
            )}

        </div>
    );
};
